import React, { useState, useRef, useEffect, useCallback } from 'react';
import { RotateCcw } from 'lucide-react';

const GRID_SIZE = 3;
const TOTAL_DOTS = 9;

export default function PatternLockCanvas({
  onComplete,
  error = '',
  disabled = false,
  title = 'Draw Pattern',
  subtitle = 'Connect at least 4 dots to unlock',
  size = 240,
}) {
  const [selectedDots, setSelectedDots] = useState([]);
  const [currentCoord, setCurrentCoord] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const containerRef = useRef(null);

  // Calculate center coordinates of each dot (0 to 8) within viewBox
  const dotCoords = useRef([]);
  const dotRadius = 14;
  const hitRadius = 24;

  const calculateCoords = useCallback(() => {
    const coords = [];
    const padding = 34;
    const step = (size - padding * 2) / (GRID_SIZE - 1);
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        coords.push({
          id: row * GRID_SIZE + col,
          x: padding + col * step,
          y: padding + row * step,
        });
      }
    }
    dotCoords.current = coords;
  }, [size]);

  useEffect(() => {
    calculateCoords();
  }, [calculateCoords]);

  // Translate client mouse/touch point to SVG coordinate space
  const getPointFromEvent = (e) => {
    if (!containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const x = ((clientX - rect.left) / rect.width) * size;
    const y = ((clientY - rect.top) / rect.height) * size;
    return { x, y };
  };

  const getNearestDot = (point) => {
    if (!point) return null;
    for (const dot of dotCoords.current) {
      const dist = Math.hypot(dot.x - point.x, dot.y - point.y);
      if (dist <= hitRadius) {
        return dot.id;
      }
    }
    return null;
  };

  const handleStart = (e) => {
    if (disabled) return;
    e.preventDefault();
    const pt = getPointFromEvent(e);
    if (!pt) return;
    const dotId = getNearestDot(pt);
    if (dotId !== null) {
      if (navigator.vibrate) navigator.vibrate(20);
      setSelectedDots([dotId]);
      setCurrentCoord(pt);
      setIsDrawing(true);
    }
  };

  const handleMove = (e) => {
    if (!isDrawing || disabled) return;
    e.preventDefault();
    const pt = getPointFromEvent(e);
    if (!pt) return;
    setCurrentCoord(pt);

    const dotId = getNearestDot(pt);
    if (dotId !== null && !selectedDots.includes(dotId)) {
      if (navigator.vibrate) navigator.vibrate(20);
      setSelectedDots((prev) => [...prev, dotId]);
    }
  };

  const handleEnd = (e) => {
    if (!isDrawing) return;
    if (e) e.preventDefault();
    setIsDrawing(false);
    setCurrentCoord(null);

    if (selectedDots.length > 0 && onComplete) {
      onComplete([...selectedDots]);
    }
  };

  // Reset if error changes
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setSelectedDots([]);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleReset = () => {
    setSelectedDots([]);
    setCurrentCoord(null);
    setIsDrawing(false);
  };

  return (
    <div className="cv-pattern-container animate-fade-in">
      {title && <div className="cv-pattern-title">{title}</div>}
      {subtitle && <div className="cv-pattern-subtitle">{subtitle}</div>}

      <div
        ref={containerRef}
        className={`cv-pattern-board ${error ? 'error animate-shake' : ''}`}
        style={{ width: size, height: size, touchAction: 'none' }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      >
        <svg
          viewBox={`0 0 ${size} ${size}`}
          width={size}
          height={size}
          className="cv-pattern-svg"
        >
          {/* Connecting Lines */}
          {selectedDots.length > 1 &&
            selectedDots.slice(0, -1).map((dotId, idx) => {
              const start = dotCoords.current[dotId];
              const nextId = selectedDots[idx + 1];
              const end = dotCoords.current[nextId];
              if (!start || !end) return null;
              return (
                <line
                  key={`line-${idx}`}
                  x1={start.x}
                  y1={start.y}
                  x2={end.x}
                  y2={end.y}
                  stroke={error ? '#ef4444' : '#10b981'}
                  strokeWidth={4}
                  strokeLinecap="round"
                  className="cv-pattern-line"
                />
              );
            })}

          {/* Active pointer follower line */}
          {isDrawing && selectedDots.length > 0 && currentCoord && (
            <line
              x1={dotCoords.current[selectedDots[selectedDots.length - 1]].x}
              y1={dotCoords.current[selectedDots[selectedDots.length - 1]].y}
              x2={currentCoord.x}
              y2={currentCoord.y}
              stroke={error ? '#ef4444' : '#10b981'}
              strokeWidth={3}
              strokeDasharray="4 4"
              strokeLinecap="round"
              opacity={0.8}
            />
          )}

          {/* 9 Dots */}
          {dotCoords.current.map((dot) => {
            const isSelected = selectedDots.includes(dot.id);
            return (
              <g key={`dot-${dot.id}`}>
                {/* Hit area ring */}
                <circle
                  cx={dot.x}
                  cy={dot.y}
                  r={isSelected ? dotRadius + 4 : dotRadius}
                  fill={isSelected ? (error ? 'rgba(239, 68, 68, 0.2)' : 'rgba(16, 185, 129, 0.25)') : 'transparent'}
                  stroke={isSelected ? (error ? '#ef4444' : '#10b981') : 'rgba(255, 255, 255, 0.15)'}
                  strokeWidth={isSelected ? 2 : 1}
                  className="cv-pattern-dot-outer"
                />
                {/* Center dot */}
                <circle
                  cx={dot.x}
                  cy={dot.y}
                  r={isSelected ? 6 : 4}
                  fill={isSelected ? (error ? '#ef4444' : '#10b981') : 'rgba(255, 255, 255, 0.65)'}
                  className="cv-pattern-dot-center"
                />
              </g>
            );
          })}
        </svg>
      </div>

      {error ? (
        <div className="cv-pattern-error-text animate-shake">{error}</div>
      ) : (
        selectedDots.length > 0 && (
          <button
            type="button"
            className="cv-pattern-reset-btn"
            onClick={handleReset}
          >
            <RotateCcw size={13} />
            <span>Reset Pattern</span>
          </button>
        )
      )}
    </div>
  );
}
